import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { User } from "@dropin/shared/schemas";
import { verificationStateOf } from "@dropin/shared/schemas";
import {
  type AdminBusiness,
  adminListBusinesses,
  adminListUsers,
  adminUpdateUser,
} from "../api";

type Tab = "pending" | "creators" | "businesses";

export function AdminDashboard() {
  const [tab, setTab] = useState<Tab>("pending");

  return (
    <div className="grid gap-5">
      <div className="inline-flex rounded-md border border-border bg-paper p-1 flex-wrap">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="admin-tab-pending">
          Pending
        </TabBtn>
        <TabBtn active={tab === "creators"} onClick={() => setTab("creators")} testid="admin-tab-creators">
          All creators
        </TabBtn>
        <TabBtn active={tab === "businesses"} onClick={() => setTab("businesses")} testid="admin-tab-businesses">
          Businesses
        </TabBtn>
      </div>

      {(tab === "pending" || tab === "creators") && (
        <CreatorsList kind={tab} />
      )}
      {tab === "businesses" && <BusinessesList />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
  testid,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testid: string;
}) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded px-3.5 py-1.5 text-sm font-semibold transition-colors ${
        active ? "bg-surface text-ink shadow-xs" : "text-muted hover:text-ink"
      }`}
    >
      {children}
    </button>
  );
}

// ---------- Creators ----------

function CreatorsList({ kind }: { kind: "pending" | "creators" }) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { users } =
        kind === "pending"
          ? await adminListUsers({ pending: true })
          : await adminListUsers({ role: "creator" });
      setUsers(users);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load");
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function decide(u: User, verified: boolean) {
    setBusyId(u.id);
    try {
      await adminUpdateUser(u.id, { verified });
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed");
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <div className="card text-rose text-sm">{error}</div>;
  if (loading) return <div className="card text-muted">Loading…</div>;
  if (users.length === 0) {
    return (
      <div className="card text-muted">
        {kind === "pending" ? "No pending requests." : "No creators yet."}
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {users.map((u) => (
        <UserCard key={u.id} user={u} busy={busyId === u.id} onDecide={(v) => decide(u, v)} />
      ))}
    </div>
  );
}

function UserCard({
  user,
  busy,
  onDecide,
}: {
  user: User;
  busy: boolean;
  onDecide: (verified: boolean) => void;
}) {
  const u = user;
  const state = verificationStateOf(u);
  const name = u.displayName || u.email;
  const initial = (name?.[0] ?? "?").toUpperCase();
  const locationLine = [u.neighborhood, u.city].filter(Boolean).join(" · ");
  return (
    <div className="card card-hover" data-testid={`admin-user-${u.id}`}>
      <Link
        to={`/admin/users/${u.id}`}
        className="block no-underline text-inherit hover:no-underline"
        data-testid={`admin-user-open-${u.id}`}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-12 w-12 rounded-full border border-border bg-paper overflow-hidden flex items-center justify-center">
            {u.avatarUrl ? (
              <img
                src={u.avatarUrl}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span className="text-lg font-semibold text-muted" aria-hidden>
                {initial}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-ink truncate">{name}</span>
              <UserStateChip state={state} />
            </div>
            {u.displayName && <p className="text-xs text-muted truncate">{u.email}</p>}
            {locationLine && <p className="text-xs text-muted truncate">{locationLine}</p>}
          </div>
        </div>

        {u.bio && <p className="mt-3 text-sm text-muted leading-relaxed">{u.bio}</p>}

        {u.socialLinks && Object.keys(u.socialLinks).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Object.entries(u.socialLinks).map(([platform, handle]) =>
              handle ? (
                <span key={platform} className="chip text-xs">
                  {platform === "twitter" ? "X" : platform.charAt(0).toUpperCase() + platform.slice(1)}: {handle}
                </span>
              ) : null,
            )}
          </div>
        )}
      </Link>

      <div className="mt-4 border-t border-border pt-3 flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs text-muted">
          {state === "pending" && u.verificationRequestedAt
            ? `Requested ${relativeTime(u.verificationRequestedAt)}`
            : state === "verified"
              ? "Currently verified"
              : "Has not requested verification"}
        </span>
        <div className="flex gap-2">
          {state === "pending" && (
            <>
              <button
                className="btn-primary text-xs"
                onClick={() => onDecide(true)}
                disabled={busy}
                data-testid={`admin-approve-${u.id}`}
              >
                {busy ? "…" : "Approve"}
              </button>
              <button
                className="btn text-xs"
                onClick={() => onDecide(false)}
                disabled={busy}
                data-testid={`admin-reject-${u.id}`}
              >
                Reject
              </button>
            </>
          )}
          {state === "verified" && (
            <button
              className="btn text-xs"
              onClick={() => onDecide(false)}
              disabled={busy}
              data-testid={`admin-revoke-${u.id}`}
            >
              {busy ? "…" : "Revoke"}
            </button>
          )}
          {state === "unverified" && (
            <button
              className="btn text-xs"
              onClick={() => onDecide(true)}
              disabled={busy}
              data-testid={`admin-force-verify-${u.id}`}
            >
              {busy ? "…" : "Verify anyway"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function UserStateChip({ state }: { state: "unverified" | "pending" | "verified" }) {
  if (state === "verified") return <span className="chip-success text-xs">Verified</span>;
  if (state === "pending") return <span className="chip-accent text-xs">Pending</span>;
  return <span className="chip text-xs">Unverified</span>;
}

// ---------- Businesses ----------

type PlanFilter = "all" | "free" | "pro";

function BusinessesList() {
  const [filter, setFilter] = useState<PlanFilter>("all");
  const [businesses, setBusinesses] = useState<AdminBusiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminListBusinesses(filter === "all" ? {} : { plan: filter })
      .then(({ businesses }) => {
        if (!cancelled) {
          setBusinesses(businesses);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  return (
    <>
      <div className="inline-flex rounded-md border border-border bg-paper p-1 self-start">
        {(["all", "free", "pro"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded px-3 py-1 text-xs font-semibold transition-colors capitalize ${
              filter === f ? "bg-surface text-ink shadow-xs" : "text-muted hover:text-ink"
            }`}
            data-testid={`admin-biz-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      {error && <div className="card text-rose text-sm">{error}</div>}
      {loading ? (
        <div className="card text-muted">Loading…</div>
      ) : businesses.length === 0 ? (
        <div className="card text-muted">No businesses match.</div>
      ) : (
        <div className="grid gap-3">
          {businesses.map((b) => (
            <BusinessCard key={b.id} biz={b} />
          ))}
        </div>
      )}
    </>
  );
}

function BusinessCard({ biz }: { biz: AdminBusiness }) {
  const initial = (biz.name?.[0] ?? "?").toUpperCase();
  const locationLine = [biz.address, biz.city].filter(Boolean).join(" · ");
  return (
    <Link
      to={`/admin/businesses/${biz.id}`}
      className="card card-hover block no-underline text-inherit hover:no-underline"
      data-testid={`admin-biz-${biz.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 h-12 w-12 rounded-lg border border-border bg-paper overflow-hidden flex items-center justify-center">
          {biz.logoUrl ? (
            <img
              src={biz.logoUrl}
              alt=""
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <span className="text-lg font-semibold text-muted">{initial}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-ink truncate">{biz.name}</span>
            <PlanChip plan={biz.plan} />
            {biz.lat == null && (
              <span className="chip text-xs text-muted" title="No geocoded location">
                ungeocoded
              </span>
            )}
          </div>
          {locationLine && <p className="text-xs text-muted truncate">{locationLine}</p>}
          {biz.ownerEmail && <p className="text-xs text-muted truncate">Owner: {biz.ownerEmail}</p>}
        </div>
      </div>
    </Link>
  );
}

function PlanChip({ plan }: { plan: "free" | "pro" }) {
  return plan === "pro" ? (
    <span className="chip-success text-xs">Pro</span>
  ) : (
    <span className="chip text-xs">Free</span>
  );
}

function relativeTime(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMin = Math.max(0, Math.floor((now - t) / 60_000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
